import type { Theme, WithStyles } from '@material-ui/core'
import { Badge, createStyles, withStyles } from '@material-ui/core'
import MenuIcon from '@material-ui/icons/Menu'
import React from 'react'
import classNames from 'classnames'
import { motion, AnimatePresence } from '../../framer-motion'
import IconButton from '../IconButton'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'fixed',
      right: 0,
      top: '33vh',
      zIndex: 2,
      '@media print': {
        display: 'none'
      }
    },
    button: {
      color: theme.palette.primary.contrastText,
      background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
      whiteSpace: 'nowrap',
      borderRadius: theme.typography.pxToRem(4),
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,

      padding: `${theme.spacing()}px ${theme.spacing()}px`,
      '& svg': {
        width: '1.5em',
        height: '1.5em',
        [theme.breakpoints.down('md')]: {
          width: '1em',
          height: '1em'
        }
      }
    }
  })

export interface SidebarButtonProps {
  classes?: WithStyles<typeof styles>['classes']
  show?: boolean
  badgeCount?: number
  onClick?: (ev: React.MouseEvent) => void
}

/**
 * Renders a Button component from material-ui but provides extra props for adding an icon
 */
function SidebarButton({ classes, badgeCount, show, onClick }: SidebarButtonProps) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="sidebar-button"
          className={classNames(classes.root, 'tour-quote-sidebar-button')}
          whileHover={{
            scale: 1.1,
            transformOrigin: 'right'
          }}
          initial={{ x: 80, transformOrigin: 'right' }}
          animate={{
            x: 0,
            transition: {
              type: 'spring',
              stiffness: 1000,
              damping: 100
            }
          }}
          exit={{
            x: 80
          }}
        >
          <Badge
            badgeContent={badgeCount}
            max={99}
            color="secondary"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
          >
            <IconButton
              data-testid="sidebar-button"
              aria-label="open sidebar"
              role="menu"
              className={classes.button}
              size={'medium'}
              onClick={onClick}
              disableRipple
            >
              <MenuIcon />
            </IconButton>
          </Badge>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default withStyles(styles)(SidebarButton)
